import React, {createContext, ReactNode, useContext, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {stripSensitiveChatText} from '/component/partial/Chat/util/chatSanitization.util';
import {getAbacusConversationResponse} from '/redux/action/abacus.action';
import {
  selectAbacusConversationId,
  selectAbacusExternalSessionId,
} from '/redux/selector/abacus.selector';
import {credentialsEmail} from '/redux/selector/auth.selector';
import {userId as selectUserId} from '/redux/selector/currentUser.selector';

import {filterDbSchemaLeakage} from '/service/abacus/contentFilter';

type ChatTopic = 'orders' | 'rank' | 'commissions' | 'bonuses';

interface ChatSendOptions {
  onPartial?: (text: string) => void;
  onProgress?: (text: string) => void;
  conversationId?: string | null;
  externalSessionId?: string | null;
  topic?: ChatTopic;
}

interface ChatSendResult {
  replyText: string;
  conversationId: string | null;
  externalSessionId: string | null;
  messageIndex?: number | null;
  isApproved?: boolean | null;
  segments?: Array<Record<string, any>> | null;
  lastSuccessfulRunTime?: string;
}

interface ChatContextType {
  isChatVisible: boolean;
  showChat: () => void;
  hideChat: () => void;
  sendMessage: (
    message: string,
    options?: ChatSendOptions,
  ) => Promise<ChatSendResult>;
  conversationId: string | null;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({children}) => {
  const [isChatVisible, setIsChatVisible] = useState(false);
  const dispatch = useDispatch<any>();
  const conversationId = useSelector(selectAbacusConversationId);
  const externalSessionId = useSelector(selectAbacusExternalSessionId);
  const loggedCoachId = useSelector(selectUserId);
  const loggedCoachEmail = useSelector(credentialsEmail);

  const buildRequestMessage = (message: string): string => {
    return stripSensitiveChatText(message);
  };

  const showChat = () => {
    setIsChatVisible(true);
  };

  const hideChat = () => {
    setIsChatVisible(false);
  };

  const normalizeText = (text: string): string =>
    text.replace(/\s+/g, ' ').trim().toLowerCase();

  const normalizeAgentStatusToken = (text: string): string =>
    text
      .trim()
      .replace(/^['"]+|['"]+$/g, '')
      .replace(/^[[({\s]+/, '')
      .replace(/[\])}\s]+$/, '')
      .trim()
      .toLowerCase();

  const AGENT_STATUS_WORDS = new Set([
    'skipped',
    'pending',
    'processing',
    'queued',
    'cancelled',
    'canceled',
    'aborted',
    'error',
    'failed',
    'timeout',
  ]);

  const isMeaningfulAnswer = (candidate: string, prompt: string): boolean => {
    const trimmedCandidate = stripSensitiveChatText(candidate).trim();
    if (trimmedCandidate.length === 0) {
      return false;
    }

    if (AGENT_STATUS_WORDS.has(normalizeAgentStatusToken(trimmedCandidate))) {
      return false;
    }

    const normalizedCandidate = normalizeText(trimmedCandidate);
    const normalizedPrompt = normalizeText(prompt);

    if (normalizedCandidate === normalizedPrompt) {
      return false;
    }

    if (
      normalizedCandidate.length <= 6 &&
      normalizedPrompt.includes(normalizedCandidate)
    ) {
      return false;
    }

    return true;
  };

  const getStringFromSegments = (
    segments: any,
    prompt: string,
  ): string | null => {
    if (!Array.isArray(segments)) {
      return null;
    }

    // Prefer the segment whose id is "final_response"
    for (const segment of segments) {
      if (
        segment &&
        typeof segment === 'object' &&
        segment.id === 'final_response' &&
        typeof segment.segment === 'string' &&
        isMeaningfulAnswer(segment.segment, prompt)
      ) {
        return segment.segment;
      }
    }

    const parts = segments
      .map(segment => {
        if (segment == null) {
          return null;
        }

        if (typeof segment === 'string') {
          return segment;
        }

        if (typeof segment !== 'object') {
          return null;
        }

        if (typeof segment.text === 'string') {
          return segment.text;
        }

        if (typeof segment.displayText === 'string') {
          return segment.displayText;
        }

        if (typeof segment.segment === 'string') {
          return segment.segment;
        }

        if (segment.segment && typeof segment.segment === 'object') {
          if (typeof segment.segment.segment === 'string') {
            return segment.segment.segment;
          }

          if (typeof segment.segment.text === 'string') {
            return segment.segment.text;
          }
        }

        if (segment.data) {
          if (typeof segment.data.text === 'string') {
            return segment.data.text;
          }

          if (typeof segment.data.displayText === 'string') {
            return segment.data.displayText;
          }
        }

        return null;
      })
      .map(value => stripSensitiveChatText(value))
      .filter((value): value is string => isMeaningfulAnswer(value, prompt));

    if (parts.length === 0) {
      return null;
    }

    return parts.join('\n');
  };

  const extractTextFromMessage = (
    message: any,
    prompt: string,
  ): string | null => {
    if (!message || typeof message !== 'object') {
      return null;
    }

    const candidates = [
      message.message,
      message.text,
      message.content,
      message.value,
    ];
    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim().length > 0) {
        const normalizedCandidate = stripSensitiveChatText(candidate).trim();
        if (isMeaningfulAnswer(normalizedCandidate, prompt)) {
          return normalizedCandidate;
        }
      }
    }

    const segmentsText = getStringFromSegments(message.segments, prompt);
    if (segmentsText) {
      return segmentsText;
    }

    if (Array.isArray(message.content)) {
      const nested = message.content
        .map((item: any) => {
          if (typeof item === 'string') {
            return item;
          }
          if (item && typeof item === 'object') {
            if (typeof item.text === 'string') {
              return item.text;
            }
            if (typeof item.value === 'string') {
              return item.value;
            }
            if (typeof item.content === 'string') {
              return item.content;
            }
          }
          return null;
        })
        .map(value => stripSensitiveChatText(value))
        .filter((value): value is string => isMeaningfulAnswer(value, prompt));
      if (nested.length > 0) {
        return nested.join('\n');
      }
    }

    return null;
  };

  const extractFromMessagesArray = (
    messages: any,
    prompt: string,
  ): string | null => {
    if (!Array.isArray(messages)) {
      return null;
    }

    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (!message || typeof message !== 'object') {
        continue;
      }

      const role = `${message.role ?? ''}`.trim().toLowerCase();
      if (
        role === 'user' ||
        role === 'human' ||
        role === 'client' ||
        role === 'customer'
      ) {
        continue;
      }

      const extracted = extractTextFromMessage(message, prompt);
      if (extracted) {
        return extracted;
      }
    }

    return null;
  };

  const deepExtractText = (
    value: any,
    visited: Set<any>,
    depth: number,
    prompt: string,
  ): string | null => {
    if (value == null) {
      return null;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      const sanitized = stripSensitiveChatText(trimmed);
      if (!sanitized) {
        return null;
      }
      const hasWhitespace = /\s/.test(sanitized);
      if (hasWhitespace || sanitized.length > 40) {
        return sanitized;
      }

      return null;
    }

    if (typeof value !== 'object' || visited.has(value)) {
      return null;
    }

    visited.add(value);

    if (Array.isArray(value)) {
      for (const item of value) {
        const extracted = deepExtractText(item, visited, depth + 1, prompt);
        if (extracted) {
          return extracted;
        }
      }
      return null;
    }

    const priorityKeys = [
      'answer',
      'message',
      'text',
      'content',
      'value',
      'result',
      'response',
      'displayText',
      'body',
      'bodyText',
      'snippet',
    ];

    for (const key of priorityKeys) {
      if (key in value) {
        const extracted = deepExtractText(
          value[key],
          visited,
          depth + 1,
          prompt,
        );
        if (extracted) {
          if (isMeaningfulAnswer(extracted, prompt)) {
            return extracted;
          }
        }
      }
    }

    for (const item of Object.values(value)) {
      const extracted = deepExtractText(item, visited, depth + 1, prompt);
      if (extracted) {
        if (isMeaningfulAnswer(extracted, prompt)) {
          return extracted;
        }
      }
    }

    return null;
  };

  const extractResultMessageText = (response: any): string | null => {
    const pickText = (message: any): string | null => {
      if (!message || typeof message !== 'object') {
        return null;
      }

      const candidates = [message.text, message.message, message.content];
      for (const candidate of candidates) {
        if (typeof candidate === 'string' && candidate.trim().length > 0) {
          const normalized = stripSensitiveChatText(candidate).trim();
          if (isMeaningfulAnswer(normalized, '')) {
            return normalized;
          }
        }
      }

      const fromSegments = getStringFromSegments(message.segments, '');
      if (fromSegments) {
        return fromSegments;
      }

      return null;
    };

    const findInMessages = (messages: any[]): string | null => {
      if (!Array.isArray(messages) || messages.length === 0) {
        return null;
      }

      // Prefer the second item if present.
      if (messages[1]) {
        const fromSecond = pickText(messages[1]);
        if (fromSecond) {
          return fromSecond;
        }
      }

      // Prefer the last assistant/non-user entry.
      for (let i = messages.length - 1; i >= 0; i -= 1) {
        const msg = messages[i];
        const isUser =
          Boolean(msg?.is_user) ||
          `${msg?.role ?? ''}`.toLowerCase() === 'user';
        if (isUser) {
          continue;
        }
        const fromMsg = pickText(msg);
        if (fromMsg) {
          return fromMsg;
        }
      }

      // Fallback to the first message with text.
      for (const msg of messages) {
        const fromMsg = pickText(msg);
        if (fromMsg) {
          return fromMsg;
        }
      }

      return null;
    };

    const resultMessages = response?.result?.messages;
    const fromResult = findInMessages(resultMessages || []);
    if (fromResult) {
      return fromResult;
    }

    const topMessages = response?.messages;
    const fromTop = findInMessages(topMessages || []);
    if (fromTop) {
      return fromTop;
    }

    return null;
  };

  const extractResponseText = (response: any, prompt: string): string => {
    if (!response) {
      return 'No response received';
    }

    if (typeof response === 'string') {
      const trimmed = stripSensitiveChatText(response).trim();
      if (!trimmed) {
        return 'No response received';
      }

      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === 'object') {
          return extractResponseText(parsed, prompt);
        }
      } catch {
        // Not a JSON payload string; fall back to direct text evaluation.
      }

      return isMeaningfulAnswer(trimmed, prompt)
        ? trimmed
        : 'No response received';
    }

    const fromResult = extractResultMessageText(response);
    if (fromResult && isMeaningfulAnswer(fromResult, prompt)) {
      return fromResult;
    }

    const fromHistory =
      extractFromMessagesArray(response?.result?.history, prompt) ||
      extractFromMessagesArray(response?.history, prompt);
    if (fromHistory && isMeaningfulAnswer(fromHistory, prompt)) {
      return fromHistory;
    }

    if (
      Array.isArray(response.responseParagraphs) &&
      response.responseParagraphs.length > 0
    ) {
      const paragraphText = response.responseParagraphs.join('\n\n');
      if (isMeaningfulAnswer(paragraphText, prompt)) {
        return paragraphText;
      }
    }

    if (
      Array.isArray(response.responseLines) &&
      response.responseLines.length > 0
    ) {
      const lineText = response.responseLines.join(' ');
      if (isMeaningfulAnswer(lineText, prompt)) {
        return lineText;
      }
    }

    const fromMessages = extractFromMessagesArray(response.messages, prompt);
    if (fromMessages && isMeaningfulAnswer(fromMessages, prompt)) {
      return fromMessages;
    }

    const fromSegments =
      getStringFromSegments(response.result?.segments, prompt) ||
      getStringFromSegments(response.segments, prompt);
    if (fromSegments) {
      return fromSegments;
    }

    const heuristic = deepExtractText(response, new Set(), 0, prompt);
    if (heuristic) {
      if (isMeaningfulAnswer(heuristic, prompt)) {
        return heuristic;
      }
    }

    return 'No response received';
  };

  const normalizeLastSuccessfulRunTime = (value: unknown): string | null => {
    if (typeof value !== 'string') {
      return null;
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const parsedDate = new Date(trimmed);
    if (Number.isNaN(parsedDate.getTime())) {
      return null;
    }

    return parsedDate.toISOString();
  };

  const extractLastSuccessfulRunTime = (
    value: any,
    visited: Set<any> = new Set(),
    depth: number = 0,
  ): string | null => {
    if (value == null || depth > 8) {
      return null;
    }

    const normalizedDirect = normalizeLastSuccessfulRunTime(value);
    if (normalizedDirect) {
      return normalizedDirect;
    }

    if (typeof value === 'string') {
      try {
        return extractLastSuccessfulRunTime(
          JSON.parse(value),
          visited,
          depth + 1,
        );
      } catch {
        return null;
      }
    }

    if (typeof value !== 'object' || visited.has(value)) {
      return null;
    }

    visited.add(value);

    if (Array.isArray(value)) {
      for (const item of value) {
        const extracted = extractLastSuccessfulRunTime(
          item,
          visited,
          depth + 1,
        );
        if (extracted) {
          return extracted;
        }
      }
      return null;
    }

    const typedValue = value as Record<string, unknown>;
    const directCandidates = [
      typedValue.lastSuccessfulRunTime,
      typedValue.last_successful_run_time,
    ];

    for (const candidate of directCandidates) {
      const extracted = normalizeLastSuccessfulRunTime(candidate);
      if (extracted) {
        return extracted;
      }
    }

    const priorityKeys = ['result', 'data', 'payload', 'meta', 'status'];
    for (const key of priorityKeys) {
      if (key in typedValue) {
        const extracted = extractLastSuccessfulRunTime(
          typedValue[key],
          visited,
          depth + 1,
        );
        if (extracted) {
          return extracted;
        }
      }
    }

    for (const candidate of Object.values(typedValue)) {
      const extracted = extractLastSuccessfulRunTime(
        candidate,
        visited,
        depth + 1,
      );
      if (extracted) {
        return extracted;
      }
    }

    return null;
  };

  const extractProgressText = (value: any): string | null => {
    if (typeof value === 'string') {
      const normalized = stripSensitiveChatText(value).trim();
      return normalized.length > 0 ? normalized : null;
    }

    if (!value || typeof value !== 'object') {
      return null;
    }

    const candidates = [
      value.progressMessage,
      value.statusMessage,
      value.stageMessage,
      value.status,
      value.stage,
    ];

    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim().length > 0) {
        const normalizedCandidate = stripSensitiveChatText(candidate).trim();
        if (normalizedCandidate) {
          return normalizedCandidate;
        }
      }
    }

    return null;
  };

  const parseIsApprovedValue = (value: unknown): boolean | null | undefined => {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'number') {
      if (value === 1) {
        return true;
      }
      if (value === 0) {
        return false;
      }
      return undefined;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true' || normalized === '1') {
        return true;
      }
      if (normalized === 'false' || normalized === '0') {
        return false;
      }
      if (
        normalized === '' ||
        normalized === 'null' ||
        normalized === 'undefined'
      ) {
        return null;
      }
    }

    return undefined;
  };

  const extractSegments = (res: any): Array<Record<string, any>> | null => {
    const segments = res?.result?.segments ?? res?.segments;
    return Array.isArray(segments) ? segments : null;
  };

  const extractIsApprovedFromSegments = (
    res: any,
  ): boolean | null | undefined => {
    const segments = extractSegments(res);
    if (!Array.isArray(segments)) {
      return undefined;
    }

    for (const segment of segments) {
      if (!segment || typeof segment !== 'object') {
        continue;
      }

      if (segment.id !== 'is_approved') {
        continue;
      }

      const candidates = [
        segment.segment,
        segment.text,
        segment.value,
        segment.data?.value,
        segment.data?.text,
      ];

      for (const candidate of candidates) {
        const parsed = parseIsApprovedValue(candidate);
        if (parsed !== undefined) {
          return parsed;
        }
      }

      return null;
    }

    return undefined;
  };

  const sendMessage = async (
    message: string,
    runtimeOptions: ChatSendOptions = {},
  ): Promise<ChatSendResult> => {
    const trimmedMessage = message.trim();
    const requestMessage = buildRequestMessage(trimmedMessage);

    if (!trimmedMessage || !requestMessage) {
      throw new Error('Message cannot be empty.');
    }

    const {
      onPartial,
      onProgress,
      conversationId: runtimeConversationId,
      externalSessionId: runtimeExternalSessionId,
    } = runtimeOptions;
    const hasRuntimeConversationId = Object.prototype.hasOwnProperty.call(
      runtimeOptions,
      'conversationId',
    );
    const hasRuntimeExternalSessionId = Object.prototype.hasOwnProperty.call(
      runtimeOptions,
      'externalSessionId',
    );
    const resolvedExternalSessionId = hasRuntimeExternalSessionId
      ? runtimeExternalSessionId ?? null
      : externalSessionId ?? null;
    const resolvedConversationId = hasRuntimeConversationId
      ? runtimeConversationId ?? null
      : conversationId ?? null;
    try {
      const action = await dispatch(
        getAbacusConversationResponse({
          message: requestMessage,
          options: {
            ...(resolvedExternalSessionId
              ? {externalSessionId: resolvedExternalSessionId}
              : {}),
            ...(resolvedConversationId
              ? {conversationId: resolvedConversationId}
              : {}),
            ...(loggedCoachId != null && loggedCoachId !== ''
              ? {allowedMcn: String(loggedCoachId)}
              : {}),
            ...(loggedCoachEmail && loggedCoachEmail.trim().length > 0
              ? {emailId: loggedCoachEmail.trim()}
              : {}),
            onPartialResponse: (aggregated: any) => {
              if (!onPartial) {
                return;
              }

              const normalizedPartialSource = extractResponseText(
                aggregated,
                trimmedMessage,
              );
              const partialText = filterDbSchemaLeakage(
                stripSensitiveChatText(normalizedPartialSource),
              );

              if (
                partialText &&
                partialText !== 'No response received' &&
                isMeaningfulAnswer(partialText, trimmedMessage)
              ) {
                onPartial(partialText);
              }
            },
            onProgressResponse: (progress: any) => {
              if (!onProgress) {
                return;
              }

              const progressText = extractProgressText(progress);
              if (progressText) {
                onProgress(progressText);
              }
            },
          },
        }),
      );

      const response = action?.payload?.response;

      const extractedReplyText = filterDbSchemaLeakage(
        stripSensitiveChatText(extractResponseText(response, trimmedMessage)),
      );
      const replyText =
        extractedReplyText &&
        extractedReplyText !== 'No response received' &&
        isMeaningfulAnswer(extractedReplyText, trimmedMessage)
          ? extractedReplyText
          : 'No response received';
      const extractDeploymentConversationIdFromSegments = (
        res: any,
      ): string | null => {
        const segments = res?.result?.segments ?? res?.segments;
        if (!Array.isArray(segments)) {
          return null;
        }

        for (const seg of segments) {
          if (!seg || typeof seg !== 'object') {
            continue;
          }

          if (
            typeof seg.deploymentConversationId === 'string' &&
            seg.deploymentConversationId.trim().length > 0
          ) {
            return seg.deploymentConversationId.trim();
          }

          if (
            seg.segment &&
            typeof seg.segment === 'object' &&
            typeof seg.segment.deploymentConversationId === 'string' &&
            seg.segment.deploymentConversationId.trim().length > 0
          ) {
            return seg.segment.deploymentConversationId.trim();
          }
        }

        return null;
      };
      const extractConversationIdFromSegments = (res: any): string | null => {
        const segments = res?.result?.segments ?? res?.segments;
        if (!Array.isArray(segments)) {
          return null;
        }
        for (const seg of segments) {
          if (
            seg &&
            typeof seg === 'object' &&
            seg.id === 'conversation_id' &&
            typeof seg.segment === 'string' &&
            seg.segment.trim().length > 0
          ) {
            return seg.segment.trim();
          }
        }
        return null;
      };

      const extractedDeploymentConversationId =
        extractDeploymentConversationIdFromSegments(response);
      const resolvedResponseConversationId =
        response?.deploymentConversationId ??
        response?.deploymentConversationID ??
        response?.deployment_conversation_id ??
        extractedDeploymentConversationId ??
        response?.conversationId ??
        response?.conversationID ??
        extractConversationIdFromSegments(response) ??
        resolvedConversationId ??
        null;
      const resolvedResponseExternalSessionId =
        response?.externalSessionId ??
        response?.externalSessionID ??
        response?.external_session_id ??
        resolvedExternalSessionId ??
        null;
      const resolvedLastSuccessfulRunTime =
        extractLastSuccessfulRunTime(response);
      const resolvedIsApproved = extractIsApprovedFromSegments(response);
      const resolvedSegments = extractSegments(response);
      const extractMessageIndex = (res: any): number | null | undefined => {
        const parse = (value: unknown): number | null | undefined => {
          if (typeof value === 'number' && Number.isFinite(value)) {
            return Math.trunc(value);
          }
          if (typeof value === 'string') {
            const trimmed = value.trim();
            if (!trimmed || trimmed === 'null' || trimmed === 'undefined') {
              return null;
            }
            const parsed = Number(trimmed);
            if (Number.isFinite(parsed)) {
              return Math.trunc(parsed);
            }
          }
          return undefined;
        };

        const directCandidates = [
          res?.messageIndex,
          res?.message_index,
          res?.result?.messageIndex,
          res?.result?.message_index,
        ];
        for (const candidate of directCandidates) {
          const parsed = parse(candidate);
          if (parsed !== undefined) {
            return parsed;
          }
        }

        const segments = res?.result?.segments ?? res?.segments;
        if (!Array.isArray(segments)) {
          return undefined;
        }

        for (const seg of segments) {
          if (!seg || typeof seg !== 'object') {
            continue;
          }

          const nested =
            seg.segment && typeof seg.segment === 'object' ? seg.segment : null;
          const segmentCandidates = [
            seg.messageIndex,
            seg.message_index,
            seg.value,
            seg.segment,
            seg.data?.value,
            seg.data?.messageIndex,
            seg.data?.message_index,
            nested?.messageIndex,
            nested?.message_index,
            nested?.value,
          ];
          for (const candidate of segmentCandidates) {
            const parsed = parse(candidate);
            if (parsed !== undefined) {
              return parsed;
            }
          }
        }

        return undefined;
      };
      const resolvedMessageIndex = extractMessageIndex(response);

      if (onPartial && replyText && replyText !== 'No response received') {
        onPartial(replyText);
      }

      return {
        replyText,
        conversationId: resolvedResponseConversationId,
        externalSessionId: resolvedResponseExternalSessionId,
        ...(resolvedMessageIndex !== undefined
          ? {messageIndex: resolvedMessageIndex}
          : {}),
        ...(resolvedIsApproved !== undefined
          ? {isApproved: resolvedIsApproved}
          : {}),
        ...(resolvedSegments ? {segments: resolvedSegments} : {}),
        ...(resolvedLastSuccessfulRunTime
          ? {lastSuccessfulRunTime: resolvedLastSuccessfulRunTime}
          : {}),
      };
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }

      throw new Error('Failed to send message');
    }
  };

  const value: ChatContextType = {
    isChatVisible,
    showChat,
    hideChat,
    sendMessage,
    conversationId: conversationId ?? externalSessionId ?? null,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChatContext = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};

export default ChatProvider;
